import React, { createContext, useContext, type ReactNode } from 'react';
import { styled } from '@compiled/react';
import { CSSTransition } from 'react-transition-group';
import { durations, easeInOut } from '@atlaskit/motion';
import { DEFAULT_SIDEBAR_WIDTH } from '../controllers/index.tsx';

export type Props = {
	isOpen: boolean;
	children: ReactNode;
	sidebarWidth?: number;
};

const SidebarContext = createContext<{ sidebarWidth: number; isOpen: boolean }>({
	sidebarWidth: 0,
	isOpen: true,
});

export const PushingSidebarContainer = ({
	sidebarWidth = DEFAULT_SIDEBAR_WIDTH,
	isOpen,
	...props
}: Props): React.JSX.Element => (
	<SidebarContext.Provider value={{ sidebarWidth, isOpen }}>
		<SidebarContainerWrapper sidebarWidth={sidebarWidth} isOpen={isOpen}>
			{props.children}
		</SidebarContainerWrapper>
	</SidebarContext.Provider>
);

export const PushingSidebar = (props: { children: ReactNode }): React.JSX.Element => {
	const { isOpen, sidebarWidth } = useContext(SidebarContext);

	return (
		<CSSTransition
			classNames="pushing-sidebar-transition"
			in={isOpen}
			timeout={durations.medium}
			unmountOnExit
		>
			<SidebarWrapper width={sidebarWidth}>{props.children}</SidebarWrapper>
		</CSSTransition>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SidebarContainerWrapper = styled.div<{
	isOpen: boolean;
	sidebarWidth: string | number;
}>(
	{
		position: 'relative',
		overflow: 'hidden',
		height: '100%',
		willChange: 'padding-right',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		transition: `${durations.medium}ms ${easeInOut} padding-right`,
	},
	// eslint-disable-next-line @typescript-eslint/no-explicit-any, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props: any) =>
		props.isOpen && {
			paddingRight: `${props.sidebarWidth}px`,
		},
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
const SidebarWrapper = styled.div<{ width: string | number }>((props) => ({
	height: '100%',
	position: 'absolute',
	top: 0,
	right: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${props.width}px`,
	/* Transition styles */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
	transition: `transform ${durations.medium}ms ${easeInOut}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'&.pushing-sidebar-transition-enter': {
		transform: 'translateX(100%)',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'&-active': {
			transform: 'translateX(0%)',
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'&.pushing-sidebar-transition-exit': {
		transform: 'translateX(0%)',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'&-active': {
			transform: 'translateX(100%)',
		},
	},
}));
